import type { PortfolioReport } from '@interfaces/portfolioReport';
import { Chip } from '@ovComponents/2-component/chip/chip';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type {
  RunReportImpactFilter,
  RunReportPortfolioFilterRow,
  RunReportPortfolioFilters,
} from '../../types/runTable.types';
import {
  buildRunReportFilterIndex,
  hasActivePortfolioFilters,
} from '../../utils/runTable/runReportPortfolioFilters';
import { BulkRebalancePortfolioFilterBar } from '../shared/portfolioFilterBar';

export type BulkRebalanceRunReportImpactFiltersProps = {
  value: RunReportImpactFilter;
  onChange: (value: RunReportImpactFilter) => void;
};

const FILTER_OPTIONS: RunReportImpactFilter[] = [
  'all',
  'passed',
  'failed',
  'liquidate',
  'transfer',
  'tax',
  'highTurnover',
];

export const BulkRebalanceRunReportImpactFilters = ({
  value,
  onChange,
}: BulkRebalanceRunReportImpactFiltersProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();

  return (
    <Box
      data-testid='bulk-rebalance-report-impact-filters'
      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}
    >
      {FILTER_OPTIONS.map((filter) => {
        const isSelected = value === filter;

        return (
          <Chip
            key={filter}
            text={t(`runInsights.reviewNext.filter.${filter}`)}
            small
            onClick={() => onChange(isSelected ? 'all' : filter)}
            sx={
              isSelected
                ? {
                    backgroundColor: theme.color.primaryVariant,
                    borderColor: theme.color.primary,
                    '&:hover': {
                      backgroundColor: theme.color.primaryVariantHover,
                      borderColor: theme.color.primary,
                    },
                  }
                : undefined
            }
          />
        );
      })}
    </Box>
  );
};

export type BulkRebalanceRunReportPortfolioFiltersProps = {
  filterScopeRows: RunReportPortfolioFilterRow[];
  reportsById: Map<string, PortfolioReport>;
  audits: OptimizerAuditV2Record[];
  tickerMap: Record<string, string>;
  value: RunReportPortfolioFilters;
  onChange: (value: RunReportPortfolioFilters) => void;
  leading?: ReactNode;
};

export const BulkRebalanceRunReportPortfolioFilters = ({
  filterScopeRows,
  reportsById,
  audits,
  tickerMap,
  value,
  onChange,
  leading,
}: BulkRebalanceRunReportPortfolioFiltersProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['bulkRebalance']);
  const filterIndex = useMemo(
    () =>
      buildRunReportFilterIndex(
        translateBackendString,
        filterScopeRows,
        audits,
        tickerMap,
        reportsById,
        value,
      ),
    [audits, filterScopeRows, reportsById, tickerMap, value],
  );

  return (
    <BulkRebalancePortfolioFilterBar
      searchPlaceholder={t('reports.filterSearchPlaceholder')}
      searchTestId='bulk-rebalance-report-filter-search'
      modelTestId='bulk-rebalance-report-filter-model'
      assetTestId='bulk-rebalance-report-filter-asset'
      clearTestId='bulk-rebalance-report-filter-clear'
      value={value}
      modelOptions={filterIndex.modelOptions}
      assetOptions={filterIndex.assetOptions}
      onChange={onChange}
      leading={leading}
    />
  );
};

export type BulkRebalanceRunReportTableHeaderProps = {
  filterScopeRows: RunReportPortfolioFilterRow[];
  reportsById: Map<string, PortfolioReport>;
  audits: OptimizerAuditV2Record[];
  tickerMap: Record<string, string>;
  impactFilter: RunReportImpactFilter;
  portfolioFilters: RunReportPortfolioFilters;
  onImpactFilterChange: (value: RunReportImpactFilter) => void;
  onPortfolioFiltersChange: (value: RunReportPortfolioFilters) => void;
  matchingCount?: number;
  globalMatchingCount?: number;
  reportsFullyLoaded?: boolean;
  totalCount?: number;
  loadedReportCount?: number;
};

export const BulkRebalanceRunReportTableHeader = ({
  filterScopeRows,
  reportsById,
  audits,
  tickerMap,
  impactFilter,
  portfolioFilters,
  onImpactFilterChange,
  onPortfolioFiltersChange,
  matchingCount,
  globalMatchingCount,
  reportsFullyLoaded = true,
  totalCount = 0,
  loadedReportCount = 0,
}: BulkRebalanceRunReportTableHeaderProps) => {
  const { t } = useTranslation(['bulkRebalance']);

  const showScopeNotice =
    !reportsFullyLoaded &&
    hasActivePortfolioFilters(portfolioFilters) &&
    totalCount > loadedReportCount;

  const showGlobalAssetNotice =
    globalMatchingCount != null &&
    matchingCount != null &&
    globalMatchingCount > matchingCount;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant='headingSmall'>
          {t('detail.reportsTitle')}
        </Typography>
        {showScopeNotice ? (
          <Typography variant='labelSmall' color='textSecondary'>
            {t('reports.filterScopeNotice', {
              loaded: loadedReportCount,
              total: totalCount,
            })}
          </Typography>
        ) : null}
        {showGlobalAssetNotice ? (
          <Typography variant='labelSmall' color='textSecondary'>
            {t('reports.filterAssetScopeNotice', {
              shown: matchingCount,
              total: globalMatchingCount,
            })}
          </Typography>
        ) : null}
      </Box>
      <BulkRebalanceRunReportPortfolioFilters
        filterScopeRows={filterScopeRows}
        reportsById={reportsById}
        audits={audits}
        tickerMap={tickerMap}
        value={portfolioFilters}
        onChange={onPortfolioFiltersChange}
        leading={
          <BulkRebalanceRunReportImpactFilters
            value={impactFilter}
            onChange={onImpactFilterChange}
          />
        }
      />
    </Box>
  );
};
